import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import usePopoverPosition from '../../hooks/usePopoverPosition';
import { useTerminalExtStore } from '../../store/terminalExtStore';
import TerminalLaunchList from './TerminalLaunchList';
import { useVisibleTerminalShells } from './useTerminalShells';
import { launchProfile, splitOrOpenTab } from './launchProfile';
import { cloneSpec } from '../../store/terminalPaneActions';
import { PaneIcon } from './TerminalPaneHeader';
import SshConnectionModal from './SshConnectionModal';
import './TerminalMenu.css';

const WIDTH = 300;

/** A ref-like anchor for usePopoverPosition: an element, or a click point. */
function toAnchorRef(anchor) {
  if (anchor && typeof anchor.getBoundingClientRect === 'function') return { current: anchor };
  const x = anchor?.x ?? 0;
  const y = anchor?.y ?? 0;
  return {
    current: {
      getBoundingClientRect: () => ({
        left: x,
        right: x,
        top: y,
        bottom: y,
        width: 0,
        height: 0,
      }),
    },
  };
}

/** What a shell row opens: that shell, in the split pane's folder. */
export function shellSplitSpec(shell, src) {
  const spec = {
    kind: 'local',
    label: shell.label,
    shellKey: shell.key,
    cwd: src?.liveCwd || src?.cwd,
  };
  if (!spec.cwd) delete spec.cwd;
  return spec;
}

/**
 * "Split right with…" / "Split down with…": the "+" menu's list for ONE
 * pane's split. First row "Same as this pane" (active by default, so Enter
 * keeps the instant duplicate), then the agents (a not-installed one shows
 * its install hint; the row's folder button asks for the split's working
 * directory instead of following the pane), the shells and "SSH connection…" (the vault picker; the
 * connection opens in the new pane — `onSsh`, see useSplitWithMenu). Portalled and fixed-positioned so the
 * pane's `overflow: hidden` never clips it. `anchor` is the clicked button
 * or a context-menu point `{ x, y }`.
 */
export default function SplitWithMenu({ paneId, dir, anchor, onClose, onSsh }) {
  const { t } = useTranslation();
  const shells = useVisibleTerminalShells(); // minus the ones hidden in Settings
  const panelRef = useRef(null);
  const anchorRef = useMemo(() => toAnchorRef(anchor), [anchor]);
  const pos = usePopoverPosition({ anchorRef, open: true, width: WIDTH });
  const src = useTerminalExtStore((s) => s.panes[paneId]);

  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      // The anchor button toggles the menu itself.
      if (anchor instanceof Element && anchor.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [anchor, onClose]);

  const done = (fn) => {
    onClose();
    fn();
  };
  const st = () => useTerminalExtStore.getState();
  const same = {
    key: 'same',
    label: t('terminalExt.splitSame', 'Same as this pane'),
    hint: src ? src.title || src.label : null,
    icon: <PaneIcon pane={src || { kind: 'local' }} />,
    onPick: () =>
      done(() => {
        const spec = cloneSpec(st().panes[paneId]);
        splitOrOpenTab(paneId, dir, spec, spec.label || 'Terminal');
      }),
  };

  const header = (
    <div className="term-menu-head">
      {dir === 'col' ? t('terminalExt.splitDownWith', 'Split down with…') : t('terminalExt.splitRightWith', 'Split right with…')}
    </div>
  );

  // A portal still bubbles through the React tree: keep clicks and right-clicks
  // in here from reaching the pane / tab that rendered the menu.
  const stop = (e) => e.stopPropagation();
  return createPortal(
    <div
      onClick={stop}
      onContextMenu={(e) => {
        e.preventDefault();
        stop(e);
      }}
    >
      <TerminalLaunchList
        panelRef={panelRef}
        className="term-menu term-split-with bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        style={{
          position: 'fixed',
          ...(pos.dir === 'up' ? { bottom: pos.bottom } : { top: pos.top }),
          left: pos.left,
          width: WIDTH,
          maxHeight: pos.maxHeight,
          zIndex: 10040,
        }}
        header={header}
        leadRows={[same]}
        shells={shells}
        agentTip=""
        agentFolderAction
        onAgent={(p, e, where) => done(() => launchProfile(p, dir, {}, { paneId, pickCwd: where === 'folder' }))}
        onShell={(shell) => done(() => splitOrOpenTab(paneId, dir, shellSplitSpec(shell, st().panes[paneId]), shell.label))}
        showSsh={!!onSsh}
        onSsh={onSsh ? () => done(() => onSsh(paneId, dir)) : undefined}
        onClose={() => {
          // Esc: back to the split button that opened the picker.
          onClose();
          if (anchor instanceof Element) anchor.focus?.();
        }}
      />
    </div>,
    document.body
  );
}

/**
 * What "SSH connection…" in a split menu opens once a connection is picked:
 * that connection in the new pane — or, when the pane has no room left, in a
 * tab of its own (openSshTab, which also focuses a pane that already hosts it).
 */
export function splitWithSsh(paneId, dir, conn) {
  const st = useTerminalExtStore.getState();
  const spec = { kind: 'ssh', label: conn.name || 'SSH', connId: conn.id };
  if (paneId && st.canSplitPane(paneId, dir).ok) {
    const id = st.splitPane(paneId, dir, spec);
    if (id) return id;
  }
  st.openSshTab(conn.id, conn.name);
  return null;
}

/**
 * State for one "Split with…" menu: `openSplitWith(paneId, dir, anchor)`
 * toggles it (a second click on the same button closes it); render
 * `splitWithMenu` anywhere — it portals itself. "SSH connection…" closes the
 * menu and opens the vault's connection picker here, so the picker outlives it.
 */
export function useSplitWithMenu() {
  const [menu, setMenu] = useState(null);
  const [sshFor, setSshFor] = useState(null); // { paneId, dir } while the connection picker is open
  const close = useCallback(() => setMenu(null), []);
  const openSplitWith = useCallback(
    (paneId, dir, anchor) => setMenu((cur) => (cur && cur.anchor === anchor && cur.dir === dir ? null : { paneId, dir, anchor })),
    []
  );
  const onSsh = useCallback((paneId, dir) => setSshFor({ paneId, dir }), []);
  const splitWithMenu = (
    <>
      {menu ? <SplitWithMenu {...menu} onClose={close} onSsh={onSsh} /> : null}
      {sshFor ? (
        <SshConnectionModal
          isOpen
          onClose={() => setSshFor(null)}
          onPick={(conn) => {
            splitWithSsh(sshFor.paneId, sshFor.dir, conn);
            setSshFor(null);
          }}
        />
      ) : null}
    </>
  );
  return { openSplitWith, splitWithMenu, closeSplitWith: close };
}
